import { useState } from "react";
import { X, Upload, Send } from "lucide-react";
import ModalOverlay from "../UI/ModalOverlay/ModalOverlay.tsx";
import styles from "./ApplyInternalOfferModal.module.scss";

interface ApplyInternalOfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { cv: File | ""; coverLetter: string }) => Promise<void>;
  isSubmitting: boolean;
}

const ApplyInternalOfferModal: React.FC<ApplyInternalOfferModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  const [cvFile, setCvFile] = useState<File | "">("");
  const [cvFileName, setCvFileName] = useState("");
  const [coverLetter, setCoverLetter] = useState("");

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCvFile(file);
      setCvFileName(file.name);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ cv: cvFile, coverLetter });
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setCvFile("");
      setCvFileName("");
      setCoverLetter("");
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay isOpen={isOpen} onClose={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <h2 className={styles.title}>Completar postulación</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Cerrar"
            disabled={isSubmitting}
          >
            <X size={20} />
          </button>
        </header>
        <p className={styles.subtitle}>
          Podés adjuntar tu CV y una carta de presentación para reforzar tu postulación. Ambos son opcionales.
        </p>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="cv" className={styles.label}>
              CV (opcional)
            </label>
            <div className={styles.fileInputWrapper}>
              <input
                id="cv"
                name="cv"
                type="file"
                className={styles.fileInput}
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
                disabled={isSubmitting}
              />
              <div className={styles.customFileInput}>
                <Upload size={18} />
                <span className={styles.fileName}>
                  {cvFileName || "Seleccionar archivo (.pdf, .doc, .docx)"}
                </span>
              </div>
            </div>
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="coverLetter" className={styles.label}>
              Carta de presentación (opcional)
            </label>
            <textarea
              id="coverLetter"
              name="coverLetter"
              className={styles.textarea}
              placeholder="Contá brevemente por qué te interesa esta oportunidad..."
              value={coverLetter}
              onChange={(e) => setCoverLetter(e.target.value)}
              rows={5}
              disabled={isSubmitting}
            />
          </div>
          <footer className={styles.footer}>
            <button type="button" className={styles.secondaryButton} onClick={handleClose} disabled={isSubmitting}>
              Cancelar
            </button>
            <button type="submit" className={styles.primaryButton} disabled={isSubmitting}>
              {isSubmitting ? "Enviando..." : "Enviar postulación"}
              <Send size={16} />
            </button>
          </footer>
        </form>
      </div>
    </ModalOverlay>
  );
};

export default ApplyInternalOfferModal;
